import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import {
  Storage as StorageIcon,
  Dns as DatabaseIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getCurrentDataSource, initFirebaseData } from '../../services/dataService';
import BackButton from '../common/BackButton';

const FirebaseManager = () => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [initStatus, setInitStatus] = useState(null);
  const currentSource = getCurrentDataSource();

  const handleInitializeCollections = async () => {
    setIsInitializing(true);
    setInitStatus(null);
    
    try {
      await initFirebaseData();
      setInitStatus('success');
      toast.success('🔥 Colecciones de Firebase inicializadas exitosamente');
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      setInitStatus('error');
      toast.error('❌ Error al inicializar Firebase: ' + error.message);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleReloadPage = () => {
    window.location.reload();
  };

  const firebaseFeatures = [
    {
      icon: <DatabaseIcon color="primary" />,
      title: 'Firestore Database',
      description: 'Base de datos NoSQL para almacenar vinos, categorías, bodegas y tipos'
    },
    {
      icon: <StorageIcon color="secondary" />,
      title: 'Firebase Storage',
      description: 'Almacenamiento de imágenes de vinos con CDN global'
    },
    {
      icon: <UploadIcon color="success" />,
      title: 'Subida de Archivos',
      description: 'Gestión automática de imágenes con compresión y optimización'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      
      <Typography variant="h4" gutterBottom>
        Gestión de Firebase
      </Typography>

      {/* Estado actual */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Estado Actual del Sistema
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body1">
              Fuente de datos activa:
            </Typography>
            <Chip 
              label={currentSource === 'firebase' ? '🔥 Firebase' : '📁 Mock Data'}
              color={currentSource === 'firebase' ? 'success' : 'warning'}
              variant="filled"
              size="small"
            />
          </Box>
          
          {currentSource === 'firebase' ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Firebase activo</strong> - Los datos se almacenan permanentemente en la nube.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Mock Data activo</strong> - Los datos son temporales y se pierden al refrescar.
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary">
            Para cambiar la fuente de datos, edita el archivo: 
            <code>client/src/services/dataService.js</code>
          </Typography>
        </CardContent>
      </Card>

      {/* Características de Firebase */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Características de Firebase
          </Typography>
          <List>
            {firebaseFeatures.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {feature.icon}
                </ListItemIcon>
                <ListItemText
                  primary={feature.title}
                  secondary={feature.description}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Inicializar colecciones */}
      {currentSource === 'firebase' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Inicializar Colecciones
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Si es la primera vez que usas Firebase o quieres recrear las colecciones con datos de ejemplo,
              puedes inicializarlas automáticamente.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isInitializing ? <CircularProgress size={16} /> : <DatabaseIcon />}
                onClick={handleInitializeCollections}
                disabled={isInitializing}
              >
                {isInitializing ? 'Inicializando...' : 'Inicializar Colecciones'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReloadPage}
              >
                Recargar Página
              </Button>
            </Box>

            {initStatus === 'success' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon />
                  <strong>¡Colecciones creadas exitosamente!</strong>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Se han creado las siguientes colecciones:
                  <br />• 5 Categorías de vino
                  <br />• 5 Bodegas españolas
                  <br />• 4 Tipos de vino
                  <br />• 6 Vinos de ejemplo
                </Typography>
              </Alert>
            )}

            {initStatus === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon />
                  <strong>Error al inicializar las colecciones</strong>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Verifica la configuración de Firebase y las reglas de Firestore.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Información sobre Mock Data */}
      {currentSource === 'mock' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cambiar a Firebase
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Para activar Firebase y disfrutar de persistencia real:
            </Typography>
            
            <Box component="ol" sx={{ pl: 2 }}>
              <li>Abre el archivo: <code>client/src/services/dataService.js</code></li>
              <li>Cambia <code>USE_FIREBASE = false</code> por <code>USE_FIREBASE = true</code></li>
              <li>Guarda el archivo</li>
              <li>Recarga la aplicación</li>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary">
              Una vez activado Firebase, las colecciones se crearán automáticamente 
              con datos de ejemplo la primera vez que accedas a la aplicación.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FirebaseManager; 